<template>
    <div class="flex flex-col w-full">

    
    <h5 class="text-xl font-medium mb-5 text-center text-[rgba(17,17,17,1)]">{{ emailLabel }}</h5>

    <div v-if="isEmailInput" class="w-full flex flex-col gap-1">
      <label class="font-normal text-xs block" style="color: rgba(82, 82, 82, 1);">
        Receiver's Email <span class="text-orange-500">*</span>
      </label>
      <input v-model="inputValue" type="email" placeholder="Enter your email id" class="w-full h-[44px] px-3 border rounded-lg focus:outline-none text-sm"
        :class="{ 'border-red-500': errors.inputValue }" @input="clearError" />
      <p v-if="errors.inputValue" class="text-red-500 text-xs">{{ errors.inputValue }}</p>
    </div>

    <div v-else-if="isPhoneInput" class="w-full flex flex-col gap-1">
      <label class="font-semibold text-sm block">
        Enter your mobile number <span class="text-orange-500">*</span>
      </label>
      <input v-model="inputValue" type="tel" placeholder="Enter your mobile number" class="w-full h-[44px] px-3 border rounded-lg focus:outline-none text-sm"
        :class="{ 'border-red-500': errors.inputValue }" @input="handlePhoneInput" maxlength="10" />
      <p v-if="errors.inputValue" class="text-red-500 text-xs">{{ errors.inputValue }}</p>
    </div>

    <div class="flex items-center gap-2 mt-3 w-[343px]">
      <UCheckbox v-model="updateProfile" size="sm" :ui="{ base: 'mt-4', border: 'border-[1.5px] border-[#0C5542]',background: 'bg-white',checked: 'bg-[#0C5542] border-[#0C5542]',size: 'w-[20px] h-[20px]'}"/>  
      <p class="text-sm mt-4 text-[rgba(17,17,17,1)]">Update this email ID on My profile</p>
    </div>

    <button class="w-full h-[40px] mt-11 bg-[#0C5542] text-white font-medium rounded-lg text-sm " @click="validateForm">
      CONTINUE
    </button>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useCart, useGiftCart, useDevice } from '#imports'
import { V3PopupComponent, V3ModalComponent } from '#components'

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  redirectToReceiver: Boolean,
  updateSenderEmail: Boolean
})
const emit = defineEmits(['close', 'success'])

const auth = useAuth();
const isAuthenticated = auth && auth.access_token;

const { isMobile } = useDevice()
const slideover = useSlideover()
const modal = useModal()
const cart = useCart()
const giftCart = useGiftCart()

const inputValue = ref('')
const errors = ref({})
const updateProfile = ref(false)

const isEmailInput = computed(() => props.data.missingField === 'showEmail')
const isPhoneInput = computed(() => props.data.missingField === 'showPhone')

const isValidUserEmail = computed(() => {
  return !!auth?.user?.email?.trim()
})

const emailLabel = computed(() => {
  return auth?.user?.email?.trim()
    ? 'Verify your email id'
    : 'Enter your email id'
})

const clearError = () => {
  errors.value.inputValue = ''
}

const handlePhoneInput = (event) => {
  inputValue.value = event.target.value.replace(/\D/g, '').slice(0, 10)
  errors.value.inputValue = ''
}

const validateForm = async () => {
  errors.value = {}

  const value = inputValue.value?.trim() || ''
  const giftItemData = props.data.giftItemData || {}

  // 1. Input Validation
  if (isEmailInput.value && !/\S+@\S+\.\S+/.test(value)) {
    errors.value.inputValue = 'Please enter a valid email address'
    return
  }

  if (isPhoneInput.value && !/^\d{10}$/.test(value)) {
    errors.value.inputValue = 'Please enter a valid 10-digit mobile number'
    return
  }

  // 2. Assign value to giftItemData
  if (isEmailInput.value) giftItemData.senderEmail = value
  else if (isPhoneInput.value) giftItemData.senderPhone = value

  const openPopup = isMobile ? slideover.open : modal.open
  const closePopup = isMobile ? slideover.close : modal.close
  const popupComponent = isMobile ? V3PopupComponent : V3ModalComponent

  const proceedToNextStep = async () => {
    const existingGiftCardId = giftCart.items[0]?.giftCard?.id
    const currentGiftCardId = giftItemData.giftCard?.id

    if (giftCart.itemCount > 0 && existingGiftCardId !== currentGiftCardId) {
      openPopup(popupComponent, {
        component: 'replace',
        closable: true,
        onSuccess: () => {
          giftCart.addItemToCart(giftItemData)
          closePopup()
          navigateTo('/collections/gift-cards/cart')
        },
        onClose: closePopup()
      })
      return
    }

    if (cart.items.length > 0) {
      openPopup(popupComponent, {
        component: 'addcart',
        data: giftItemData,
        closable: true,
        onSuccess: () => {
          cart.clear()
          giftCart.addItemToCart(giftItemData)
          closePopup()
          navigateTo('/collections/gift-cards/cart')
        },
        onClose: closePopup()
      })
      return
    }

    // No conflicts – directly add
    giftCart.addItemToCart(giftItemData)
    navigateTo('/collections/gift-cards/cart')
  }

  // 3. Optional profile update
  const updateUserProfile = async () => {
    const result = await $apiServer('/account/profile', {
      method: 'post',
      headers: { Authorization: `Bearer ${auth.access_token}` },
      body: { email: value }
    })

    if (result.data?.toLowerCase() === 'success') {
      const profile = await $apiServer('/account/profile', {
        headers: { Authorization: `Bearer ${auth.access_token}` }
      })

      auth.setAccessToken(auth.access_token)
      auth.setUser(profile.data)
      return true
    } else {
      console.warn('Profile update failed', result)
      return false
    }
  }

  // 4. Main Flow Control
  try {
    const needsProfileUpdate = updateProfile.value

    if (props?.data?.updateSenderEmail) {
      if (!needsProfileUpdate || await updateUserProfile()) {
        await emit('success', giftItemData)
      }
      return
    }

    if (!needsProfileUpdate || await updateUserProfile()) {
      await proceedToNextStep()
    }
  } catch (err) {
    console.error("Error during validation flow:", err)
  }
}


onMounted(() => {
  const senderEmail = props?.data?.giftItemData?.senderEmail?.trim()
  const userEmail = auth?.user?.email?.trim()

  if (isAuthenticated) {
    if (userEmail) {
      inputValue.value = senderEmail || userEmail
    } else {
      updateProfile.value = true  
    }
  }
  
})

</script>
